import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectSiteId } from '@store/selectors/site.selectors';
import { useAuth } from '@contexts/useAuth';
import type { IAdminUser } from '@apis/adminUsers';
import { Button } from '@components/ui/Button/Button';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { PlusIcon, SearchIcon } from '@/utils/icons';
import { useAdminProfessions } from '../ProfessionsTab/hooks/useAdminProfessions';
import {
  useAdminUserLanguage,
  useAdminUsers,
  useCreateAdminUser,
  useDeleteAdminUser,
  useResendInvitation,
  useSendPasswordReset,
  useUpdateAdminUser,
} from './hooks/useAdminUsers';
import { UsersTable } from './UsersTable/UsersTable';
import { UserDialog } from './UserDialog/UserDialog';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import styles from '../AdminPage.module.css';

export function UsersTab() {
  const { t } = useTranslation();
  const siteId = useSelector(selectSiteId);
  const { user: currentUser } = useAuth();
  const { toasts, push, dismiss } = useToasts();

  const { data, isLoading, isError } = useAdminUsers(siteId ?? null);
  const users = useMemo(() => data?.users ?? [], [data]);
  // Feeds the viewer-professions subtext in the table and the picker in the dialog.
  const { data: professionsData } = useAdminProfessions(siteId ?? null);
  const professions = useMemo(() => professionsData?.professions ?? [], [professionsData]);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<IAdminUser | null>(null);
  const [deleting, setDeleting] = useState<IAdminUser | null>(null);

  // Stored UI language of the user being edited — the dialog's prefill.
  const editingLanguage = useAdminUserLanguage(siteId ?? null, editing?.username ?? null);

  const createMutation = useCreateAdminUser(siteId ?? null, push);
  const updateMutation = useUpdateAdminUser(siteId ?? null, push);
  const deleteMutation = useDeleteAdminUser(siteId ?? null, push);
  const resetMutation = useSendPasswordReset(siteId ?? null, push);
  const resendMutation = useResendInvitation(siteId ?? null, push);
  const deletingName = deleting?.name ?? deleting?.username;

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((u) =>
      [u.username, u.name, u.email].some((v) => v?.toLowerCase().includes(query)),
    );
  }, [users, search]);

  if (!siteId) return null;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <PageHeader
          title={t('adminUsers.title')}
          subtitle={t('adminUsers.subtitle')}
        />
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchIcon size={15} />
            <input
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('adminUsers.searchPlaceholder')}
            />
          </div>
          <span className={styles.count}>{t('adminUsers.count', { count: visible.length })}</span>
          <div className={styles.toolbarSpacer} />
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <PlusIcon size={14} />
            {t('adminUsers.add')}
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <UsersTable
          users={visible}
          professions={professions}
          currentUserId={currentUser?.id ?? null}
          emptyMessage={
            isError
              ? t('adminUsers.loadError')
              : isLoading
                ? t('adminUsers.loading')
                : search.trim()
                  ? t('adminUsers.noMatches')
                  : t('adminUsers.empty')
          }
          onEdit={setEditing}
          onDelete={setDeleting}
          onResetPassword={(user) => {
            // One reset mail per click — a double click must not send two.
            if (resetMutation.isPending) return;
            resetMutation.mutate(user);
          }}
          onResendInvitation={(user) => {
            // Same guard: one invitation mail per click.
            if (resendMutation.isPending) return;
            resendMutation.mutate(user);
          }}
        />
      </div>

      {createOpen && (
        <UserDialog
          open
          user={null}
          professions={professions}
          submitting={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(draft) => {
            if (createMutation.isPending) return;
            createMutation.mutate(draft, { onSuccess: () => setCreateOpen(false) });
          }}
        />
      )}
      {editing && (
        <UserDialog
          key={editing.id}
          open
          user={editing}
          professions={professions}
          initialLanguage={editingLanguage}
          submitting={updateMutation.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(draft) => {
            if (updateMutation.isPending) return;
            updateMutation.mutate(
              { username: editing.username, draft },
              { onSuccess: () => setEditing(null) },
            );
          }}
        />
      )}
      {deleting && (
        <DiscardModal
          open
          title={t('adminUsers.deleteConfirmTitle', { name: deletingName })}
          description={t('adminUsers.deleteConfirmBody', { name: deletingName })}
          loading={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          // DiscardModal disables its confirm button while `loading`, so no double submit.
          onConfirm={() =>
            deleteMutation.mutate(deleting.username, { onSettled: () => setDeleting(null) })
          }
        />
      )}

      <Toasts toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
